import React from 'react';

const NewPageHeader = ({
    title = "Making Healthcare Prices",
    highlightedText = "Transparent",
    subtitle = "Discover who we are, why we exist, and how we're making fair prescription pricing a reality for millions of people worldwide.",
    backgroundColor = "#0a1628",
    gridColor = "rgba(26, 58, 92, 0.3)",
    highlightColor = "#43C5B9",
    backgroundImage = "newpage.jpg",
    flagImage = "flag.jpg",
    showFlagImage = true
}) => {
    const styles = {
        container: {
            position: 'relative',
            width: '100%',
            minHeight: '80vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            overflow: 'hidden',
            backgroundColor: backgroundColor,
        },
        backgroundImageLayer: {
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundImage: backgroundImage ? `url(${backgroundImage})` : 'none',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
            zIndex: 0
        },
        darkOverlay: {
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            zIndex: 1
        },
        glowingOrb1: {
            position: 'absolute',
            top: '25%',
            left: '25%',
            width: '384px',
            height: '384px',
            borderRadius: '50%',
            opacity: 0.1,
            filter: 'blur(80px)',
            background: `radial-gradient(circle, ${highlightColor}, transparent)`,
            animation: 'pulse 4s ease-in-out infinite',
            zIndex: 2
        },
        glowingOrb2: {
            position: 'absolute',
            bottom: '25%',
            right: '25%',
            width: '384px',
            height: '384px',
            borderRadius: '50%',
            opacity: 0.1,
            filter: 'blur(80px)',
            background: `radial-gradient(circle, ${highlightColor}, transparent)`,
            animation: 'pulse 4s ease-in-out infinite 2s',
            zIndex: 2
        },
        contentContainer: {
            position: 'relative',
            zIndex: 10,
            maxWidth: '896px',
            margin: '0 auto',
            padding: '0 24px',
            textAlign: 'center'
        },
        title: {
            fontSize: '72px',
            fontWeight: 'bold',
            color: 'white',
            marginBottom: '24px',
            lineHeight: '1.2'
        },
        highlightedSpan: {
            display: 'inline-block',
            color: highlightColor,
            textShadow: `0 0 30px ${highlightColor}80, 0 0 60px ${highlightColor}40`
        },
        subtitle: {
            fontSize: '25px',
            color: '#FFFFFF',
            margin: '0 auto',
            lineHeight: '1.75'
        },
        // ✅ Updated Responsive Flag Image
        flagImageContainer: {
            position: 'absolute',
            top: '10%',            // instead of 50%, push it down
            right: '10%',
            transform: 'translateY(0)', // remove vertical centering
            maxWidth: '40%',
            height: 'auto',
            zIndex: 3,
            opacity: 0.4,
            borderRadius: '8px',
            overflow: 'hidden',
            boxShadow: `0 0 30px ${highlightColor}40`
        },

        flagImage: {
            width: '100%',
            height: 'auto',
            objectFit: 'cover',
            objectPosition: 'center',
            transform: "scaleX(-1)"
        }
    };

    // Responsive styles
    const mediaStyles = `
        @media (min-width: 768px) {
            .title {
                font-size: 3.75rem;
            }
            .subtitle {
                font-size: 1.25rem;
            }
        }
        @media (min-width: 1024px) {
            .title {
                font-size: 4.5rem;
            }
            .subtitle {
                font-size: 1.5rem;
            }
        }
        /* ✅ Updated Flag Responsiveness */
        @media (max-width: 1024px) {
            .flag-container {
                max-width: 30%;
                right: 5%;
            }
        }
        @media (max-width: 768px) {
            .flag-container {
                display: none;
            }
        }
    `;

    return (
        <div style={styles.container}>
            {/* Background Image Layer */}
            {backgroundImage && <div style={styles.backgroundImageLayer} />}

            {/* Dark Overlay */}
            <div style={styles.darkOverlay} />

            {/* Glowing Orbs */}
            <div style={styles.glowingOrb1} />
            <div style={styles.glowingOrb2} />

            {/* Flag Image */}
            {showFlagImage && flagImage && (
                <div className="flag-container" style={styles.flagImageContainer}>
                    <img
                        src={flagImage}
                        alt="Flag"
                        style={styles.flagImage}
                    />
                </div>
            )}

            {/* Content Container */}
            <div style={styles.contentContainer}>
                <h1 className="title" style={styles.title}>
                    {title}{' '}
                    <span style={styles.highlightedSpan}>
                        {highlightedText}
                    </span>
                </h1>
                <p className="subtitle" style={styles.subtitle}>
                    {subtitle}
                </p>
            </div>

            {/* CSS Animations */}
            <style>{`
                @keyframes pulse {
                    0%, 100% {
                        transform: scale(1);
                        opacity: 0.1;
                    }
                    50% {
                        transform: scale(1.1);
                        opacity: 0.15;
                    }
                }
                ${mediaStyles}
            `}</style>
        </div>
    );
};

export default NewPageHeader;
